import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { stats } from '@fiverr-private/obs';
import { getContext } from '@fiverr-private/fiverr_context';
import { messagesApi } from '../../../utils/api';
import { validateForm } from '../validation';
import { STATS_METRIC_PREFIX } from '../../constants';
import { SEND_MESSAGE, SEND_MESSAGE_SUCCESS } from '../../metrics';
import { sanitizeMessageBody } from '../../utils/xss';
import { useStaticContext, selectRecipientInfo, selectRelatedGigId, selectSendMessage, selectSellerName, selectProfessionRelatedTranslationKey } from './static';
import { useMessage } from './message';
import { useAttachments } from './attachments';

const ValidationContext = React.createContext();

export const ValidationContextProvider = ({ children }) => {
    const staticContext = useStaticContext();
    const relatedGigId = selectRelatedGigId(staticContext);
    const professionRelatedTranslationKey = selectProfessionRelatedTranslationKey(staticContext);
    const sendMessage = selectSendMessage(staticContext);
    const { username } = selectRecipientInfo(staticContext);
    const sellerName = selectSellerName(staticContext);

    const { message, setSentMessageData } = useMessage();
    const { attachments } = useAttachments();

    const focusRef = React.useRef(null);

    const [isSending, setSending] = React.useState(false);
    const [isFocused, setFocused] = React.useState(true);
    const [dirty, setDirty] = React.useState(false);
    const [submitted, setSubmitted] = React.useState(false);
    const [showErrors, setShowErrors] = React.useState(false);

    const focus = React.useCallback(() => setFocused(true), [setFocused]);
    const clearFocus = React.useCallback(() => setFocused(false), [setFocused]);

    const [errors, warnings] = React.useMemo(
        () => {
            const { errors, warnings } = validateForm({
                message,
                attachments,
                sellerName
            });

            return [
                Object.values(errors).reduce((acc, error) => [...acc, ...error], []),
                Object.values(warnings).reduce((acc, warning) => [...acc, ...warning], [])
            ];
        },
        [message, attachments, sellerName]
    );

    React.useEffect(() => {
        !isEmpty(message) && setDirty(true);
    }, [message, setDirty]);

    React.useEffect(() => {
        if (dirty && !isEmpty(errors)) {
            setShowErrors(true);
        }
    }, [dirty, errors]);

    const sendMessageHandler = React.useCallback(async({
        username,
        relatedGigId,
        professionRelatedTranslationKey,
        attachmentsIds,
        message,
        sendBatchBrief
    }) => {
        if (sendMessage) {
            return sendMessage({ username, relatedGigId, attachmentsIds, message, sendBatchBrief, professionRelatedTranslationKey });
        }

        return messagesApi.create({
            recipientUsername: username,
            relatedGigId,
            professionRelatedTranslationKey,
            pendingAttachmentIds: attachmentsIds,
            body: message
        });
    },
    [sendMessage]);

    const submitHandler = React.useCallback(
        async({ message, attachments, onSubmitSuccess, onSubmitFailure, sendBatchBrief }) => {
            setSubmitted(true);

            if (errors.length > 0) {
                return;
            }

            const { userId } = getContext();
            const attachmentsIds = attachments.map((attachment) => attachment.pendingAttachmentId);

            try {
                setSending(true);

                const start = Date.now();
                const sanitizedMessage = sanitizeMessageBody(message);

                const { data } = await sendMessageHandler({
                    userId,
                    username,
                    relatedGigId,
                    professionRelatedTranslationKey,
                    attachmentsIds,
                    message: sanitizedMessage,
                    sendBatchBrief
                });

                setSentMessageData(data);

                stats.time(STATS_METRIC_PREFIX, SEND_MESSAGE, Date.now() - start);
                stats.count(STATS_METRIC_PREFIX, SEND_MESSAGE_SUCCESS);

                onSubmitSuccess();
            } catch (error) {
                onSubmitFailure(error);
            } finally {
                setSending(false);
            }
        },
        [relatedGigId, username, sendMessageHandler, errors.length, setSentMessageData, professionRelatedTranslationKey]
    );

    return (
        <ValidationContext.Provider value={{
            focusFlag: { isFocused, focus, clearFocus, focusRef },
            errorFlag: { errors, warnings, showErrors },
            sendingFlag: { isSending, setSending },
            submission: { submitted },
            submitHandler
        }}>
            {children}
        </ValidationContext.Provider>
    );
};

ValidationContextProvider.propTypes = {
    children: PropTypes.node.isRequired
};

export const useValidationContext = () => React.useContext(ValidationContext);

export const selectFocusFlag = (context) => context.focusFlag;
export const selectErrorFlag = (context) => context.errorFlag;
export const selectSendingFlag = (context) => context.sendingFlag;
export const selectSubmitHandler = (context) => context.submitHandler;

export const selectSubmitted = (context) => {
    const { submitted } = context.submission;

    return submitted;
};
